import React, { useState, useEffect } from 'react';
import { Card, Tree, Typography, Space, Tag, Empty, Spin, Button, Tooltip } from 'antd';
import { 
  FolderOutlined, 
  FileTextOutlined, 
  CheckSquareOutlined,
  ClearOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { Text, Title } = Typography;

function DocumentSelector({ selectedDocuments, selectedFolders, onSelectionChange }) {
  const [loading, setLoading] = useState(true);
  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [documentMap, setDocumentMap] = useState({});
  const [folderMap, setFolderMap] = useState({});

  useEffect(() => {
    fetchDocumentsAndFolders();
  }, []);

  useEffect(() => {
    const keys = [];
    if (selectedDocuments) {
      keys.push(...selectedDocuments.map(id => `doc-${id}`));
    }
    if (selectedFolders) {
      keys.push(...selectedFolders.map(id => `folder-${id}`));
    }
    setCheckedKeys(keys);
  }, [selectedDocuments, selectedFolders]);

  const fetchDocumentsAndFolders = async () => {
    try {
      setLoading(true);
      const [foldersRes, documentsRes] = await Promise.all([
        api.get('/folders').catch(() => ({ data: { folders: [] } })),
        api.get('/documents').catch(() => ({ data: { documents: [] } }))
      ]);

      // Handle both array and object response formats
      const foldersData = foldersRes.data;
      const documentsData = documentsRes.data;
      
      const folders = Array.isArray(foldersData) ? foldersData : (foldersData?.folders || []);
      const documents = Array.isArray(documentsData) ? documentsData : (documentsData?.documents || []);

      const docMap = {};
      documents.forEach(doc => {
        docMap[`doc-${doc.id}`] = doc;
      });
      setDocumentMap(docMap);

      const fldMap = {};
      folders.forEach(folder => {
        fldMap[`folder-${folder.id}`] = folder;
      });
      setFolderMap(fldMap);

      const tree = buildTree(folders, documents);
      setTreeData(tree);

      const allFolderKeys = folders.map(f => `folder-${f.id}`);
      setExpandedKeys(['root', ...allFolderKeys]);
    } catch (error) {
      console.error('Failed to fetch documents and folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildTree = (folders, documents) => {
    const folderMap = {};
    folders.forEach(folder => {
      folderMap[folder.id] = {
        key: `folder-${folder.id}`,
        title: (
          <Space>
            <FolderOutlined style={{ color: '#0B3D91' }} />
            <Text>{folder.name}</Text>
            <Tag size="small" color="blue">{folder.document_count || 0} docs</Tag>
          </Space>
        ),
        children: [],
        isLeaf: false
      };
    });

    folders.forEach(folder => {
      if (folder.parent_folder_id && folderMap[folder.parent_folder_id]) {
        folderMap[folder.parent_folder_id].children.push(folderMap[folder.id]);
      }
    });

    documents.forEach(doc => {
      const docNode = {
        key: `doc-${doc.id}`,
        title: (
          <Space>
            <FileTextOutlined style={{ color: doc.is_processed ? '#16A34A' : '#D97706' }} />
            <Text>{doc.original_filename}</Text>
            {doc.is_processed ? (
              <Tag size="small" color="success">{doc.chunk_count} chunks</Tag>
            ) : (
              <Tag size="small" color="processing">Processing...</Tag>
            )}
          </Space>
        ),
        isLeaf: true,
        disabled: !doc.is_processed
      };

      if (doc.folder_id && folderMap[doc.folder_id]) {
        folderMap[doc.folder_id].children.push(docNode);
      } else {
        folderMap[`unfiled-${doc.id}`] = docNode;
      }
    });

    const rootFolders = folders
      .filter(f => !f.parent_folder_id)
      .map(f => folderMap[f.id]);

    const unfiledDocs = documents
      .filter(d => !d.folder_id)
      .map(d => folderMap[`unfiled-${d.id}`] || {
        key: `doc-${d.id}`,
        title: (
          <Space>
            <FileTextOutlined style={{ color: d.is_processed ? '#16A34A' : '#D97706' }} />
            <Text>{d.original_filename}</Text>
            {d.is_processed ? (
              <Tag size="small" color="success">{d.chunk_count} chunks</Tag>
            ) : (
              <Tag size="small" color="processing">Processing...</Tag>
            )}
          </Space>
        ),
        isLeaf: true,
        disabled: !d.is_processed
      });

    const tree = [];
    
    if (rootFolders.length > 0) {
      tree.push(...rootFolders);
    }
    
    if (unfiledDocs.length > 0) {
      tree.push({
        key: 'unfiled',
        title: (
          <Space>
            <FolderOutlined style={{ color: '#6B7280' }} />
            <Text type="secondary">Unfiled Documents</Text>
            <Tag size="small">{unfiledDocs.length}</Tag>
          </Space>
        ),
        children: unfiledDocs,
        isLeaf: false
      });
    }

    return tree;
  };

  const handleCheck = (checked, info) => {
    setCheckedKeys(checked);
    
    const selectedDocIds = checked
      .filter(key => key.startsWith('doc-'))
      .map(key => parseInt(key.replace('doc-', '')));
    
    const selectedFolderIds = checked
      .filter(key => key.startsWith('folder-'))
      .map(key => parseInt(key.replace('folder-', '')));
    
    onSelectionChange({ documentIds: selectedDocIds, folderIds: selectedFolderIds });
  };

  const handleSelectAll = () => {
    const allDocKeys = Object.keys(documentMap).filter(key => {
      const doc = documentMap[key];
      return doc && doc.is_processed;
    });
    const allFolderKeys = Object.keys(folderMap);
    const allKeys = [...allDocKeys, ...allFolderKeys];
    setCheckedKeys(allKeys);
    
    const selectedDocIds = allDocKeys.map(key => parseInt(key.replace('doc-', '')));
    const selectedFolderIds = allFolderKeys.map(key => parseInt(key.replace('folder-', '')));
    onSelectionChange({ documentIds: selectedDocIds, folderIds: selectedFolderIds });
  };

  const handleClearAll = () => {
    setCheckedKeys([]);
    onSelectionChange({ documentIds: [], folderIds: [] });
  };

  const getSelectionSummary = () => {
    const selectedDocCount = checkedKeys.filter(k => k.startsWith('doc-')).length;
    const selectedFolderCount = checkedKeys.filter(k => k.startsWith('folder-')).length;
    const totalDocs = Object.keys(documentMap).length;
    const totalFolders = Object.keys(folderMap).length;
    
    if (selectedDocCount === 0 && selectedFolderCount === 0) {
      return 'All documents will be searched';
    }
    
    const parts = [];
    if (selectedFolderCount > 0) {
      parts.push(`${selectedFolderCount} folder${selectedFolderCount !== 1 ? 's' : ''}`);
    }
    if (selectedDocCount > 0) {
      parts.push(`${selectedDocCount} document${selectedDocCount !== 1 ? 's' : ''}`);
    }
    return parts.join(' + ') + ' selected';
  };

  if (loading) {
    return (
      <Card bordered={false} size="small">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="small" />
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary">Loading documents...</Text>
          </div>
        </div>
      </Card>
    );
  }

  if (treeData.length === 0) {
    return (
      <Card bordered={false} size="small">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No documents uploaded yet"
        />
      </Card>
    );
  }

  return (
    <Card 
      bordered={false}
      size="small"
      title={
        <Space>
          <CheckSquareOutlined style={{ color: '#0B3D91' }} />
          <Text strong>Document Selection</Text>
          <Tooltip title="Select specific documents to search. Leave unchecked to search all documents.">
            <InfoCircleOutlined style={{ color: '#6B7280', fontSize: '14px' }} />
          </Tooltip>
        </Space>
      }
      extra={
        <Space size="small">
          <Button size="small" onClick={handleSelectAll} icon={<CheckSquareOutlined />}>
            All
          </Button>
          <Button size="small" onClick={handleClearAll} icon={<ClearOutlined />}>
            Clear
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: '12px' }}>
        <Tag color={checkedKeys.length > 0 ? 'blue' : 'default'}>
          {getSelectionSummary()}
        </Tag>
      </div>
      
      <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '6px', padding: '8px' }}>
        <Tree
          checkable
          selectable={false}
          checkedKeys={checkedKeys}
          expandedKeys={expandedKeys}
          onExpand={setExpandedKeys}
          onCheck={handleCheck}
          treeData={treeData}
          style={{ fontSize: '13px' }}
        />
      </div>
    </Card>
  );
}

export default DocumentSelector;
